/**
 * Keyboard navigation for the entry form.
 *
 * The form navigates on two levels: Tab moves between *fields*, the left/right arrows move between the inputs *inside*
 * the current field. Native tabbing only knows the flat list of inputs, so on a clinical form a single question built
 * from a value, a unit and three checkboxes costs five Tab presses before the next question — the two levels keep the
 * outer loop one-press-per-question while leaving every input reachable.
 *
 * The keydown handler lives here rather than in Entry.vue so it can be tested against a DOM without mounting the whole
 * entry page. Everything it needs to know about the form model arrives through `FieldKeydownOptions`.
 */

/** Marks the element that wraps one field. EntryBaseItem stamps both onto its root. */
export const FIELD_IDENTIFIER_ATTRIBUTE = 'data-field-identifier';
export const FIELD_TYPE_ATTRIBUTE = 'data-field-type';

export type HorizontalArrow = 'ArrowLeft' | 'ArrowRight';
export type VerticalArrow = 'ArrowUp' | 'ArrowDown';

/** The part of a form field this module cares about. `FormField` satisfies it without being imported — the barrel it
 * lives in is circular, and none of the rest of a field matters here. */
export interface NavigableField {
  identifier: string;
  type: string;
  /** False for the fields of a collapsed section: still in the DOM, but hidden. */
  expanded?: boolean;
}

export interface FieldKeydownOptions {
  /** The fields in rendered order — the same array the virtualized list is given. */
  fields: () => NavigableField[];
  /** Scrolls the virtualized list until the field at `index` is rendered. */
  scrollToIndex: (index: number) => void;
  /** Field types with no input worth landing on, which Tab passes over. */
  skipTypes: string[];
  /** Field types that are grids in their own right, where Tab walks the cells before leaving the field. */
  gridTypes: string[];
  /** True when the form is read-only, in which case tabbing is left to the browser. */
  isReadOnly: () => boolean;
  /** Where to look for field elements. Defaults to the document; the tests pass their own root. */
  root?: () => Document | HTMLElement;
}

const FOCUSABLE_SELECTOR = [
  'input:not([type="hidden"])',
  'select',
  'textarea',
  'button',
  'a[href]',
  '[tabindex]',
  '[contenteditable="true"]'
].join(',');

/** Text-ish inputs, where the arrow keys belong to the caret until it reaches the end of the value. PrimeVue's
 * InputNumber is one of them — it renders a `type="text"` input rather than a number spinner. */
const TEXT_INPUT_TYPES = [
  'text',
  'search',
  'url',
  'tel',
  'email',
  'password',
  'number'
];

const isDisabled = (element: HTMLElement) =>
  element.hasAttribute('disabled') || element.getAttribute('aria-disabled') === 'true';

/**
 * Drops the inputs of collapsed sections and of `v-show`-hidden fields, which stay in the DOM.
 *
 * `display` does not inherit, so a hidden ancestor has to be looked for; `visibility` does, so the element's own
 * computed value is the answer and walking up would be wrong. That distinction is load-bearing here: virtua renders
 * its list container with `visibility: hidden` and turns each row back to `visible`, so treating a hidden ancestor as
 * final would hide every input on the form.
 */
const isRendered = (element: HTMLElement) => {
  const { visibility } = window.getComputedStyle(element);
  if (visibility === 'hidden' || visibility === 'collapse') {
    return false;
  }

  let node: HTMLElement | null = element;
  while (node) {
    if (window.getComputedStyle(node).display === 'none') {
      return false;
    }
    node = node.parentElement;
  }
  return true;
};

/** The field's inputs in tab order. */
export const getFocusableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    element => !isDisabled(element) && element.tabIndex > -1 && isRendered(element)
  );

export const isTextEntry = (
  element: HTMLElement
): element is HTMLInputElement | HTMLTextAreaElement => {
  if (element instanceof HTMLTextAreaElement) {
    return true;
  }
  return element instanceof HTMLInputElement && TEXT_INPUT_TYPES.includes(element.type);
};

/** A dropdown, multi-select or date picker with its panel showing. Every key the user presses is the panel's — it is
 * moving through its own options, and Tab moves into it. */
export const hasOpenOverlay = (element: HTMLElement) =>
  element.getAttribute('aria-expanded') === 'true';

/** True while the pressed arrow still has work to do inside the focused control, in which case the form leaves it
 * alone: a caret that has not reached the end of the value, a slider that steps on arrows, or a control whose overlay
 * is open and is navigating its own options. */
export const consumesArrowKey = (element: HTMLElement, key: HorizontalArrow): boolean => {
  if (hasOpenOverlay(element)) {
    return true;
  }
  if (element.getAttribute('role') === 'slider' || element.closest('.p-slider') !== null) {
    return true;
  }
  if (!isTextEntry(element)) {
    return false;
  }

  const { selectionStart, selectionEnd, value } = element;
  // A null selection means the browser will not tell us where the caret is (some input types refuse); assume the
  // control still wants the key rather than yanking focus out from under the user.
  if (selectionStart === null || selectionEnd === null) {
    return true;
  }
  if (selectionStart !== selectionEnd) {
    return true;
  }
  return key === 'ArrowLeft' ? selectionStart > 0 : selectionEnd < value.length;
};

/** Enters a text input from the side the user arrived from, so a left-arrow run keeps travelling left. */
export const placeCaret = (element: HTMLElement, position: 'start' | 'end') => {
  if (!isTextEntry(element)) {
    return;
  }
  const offset = position === 'start' ? 0 : element.value.length;
  try {
    element.setSelectionRange(offset, offset);
  } catch {
    // Inputs that do not support a selection range have nothing to place.
  }
};

/**
 * Index of the next input to focus within a field, or `undefined` when the arrow points past the field's edge — the
 * caller lets the key through so the edges stay quiet rather than wrapping around.
 */
export const nextInputIndex = (
  focusables: HTMLElement[],
  current: HTMLElement,
  key: HorizontalArrow
): number | undefined => {
  const position = focusables.indexOf(current);
  if (position === -1) {
    return undefined;
  }
  const next = position + (key === 'ArrowRight' ? 1 : -1);
  return next >= 0 && next < focusables.length ? next : undefined;
};

/**
 * The input one row up or down in the same column of a table field, or `undefined` when there is none — the column
 * ends, or the focused control is not in a table at all.
 *
 * A table is the one field where "the next input" is genuinely two-dimensional, and Tab and the horizontal arrows only
 * travel along the row. The plain vertical arrows cannot be used for the other axis: in a numeric column they belong
 * to the input, which steps its value with them. Shift is what is left.
 */
export const verticalCellTarget = (
  element: HTMLElement,
  key: VerticalArrow,
  field: HTMLElement
): HTMLElement | undefined => {
  const cell = element.closest('td, th');
  const row = cell?.closest('tr');
  if (!cell || !row || !field.contains(row)) {
    return undefined;
  }

  const columnIndex = (cell as HTMLTableCellElement).cellIndex;
  const forward = key === 'ArrowDown';
  // Siblings only, so a column stops at the ends of its own `tbody` rather than stepping into the header.
  let candidate = forward ? row.nextElementSibling : row.previousElementSibling;

  while (candidate) {
    const target = (candidate as HTMLTableRowElement).cells?.[columnIndex];
    const focusables = target ? getFocusableElements(target as HTMLElement) : [];
    if (focusables.length > 0) {
      return focusables[0];
    }
    // A row with nothing focusable in this column — a spacer or a read-only cell — is passed over, not treated as
    // the end of the column.
    candidate = forward ? candidate.nextElementSibling : candidate.previousElementSibling;
  }
  return undefined;
};

const nextFrame = () => new Promise(resolve => requestAnimationFrame(resolve));

/**
 * Takes a key the form is acting on away from the control it was pressed in.
 *
 * `preventDefault` alone is not enough for the arrows: PrimeVue's InputNumber steps its value from its own keydown
 * listener on the input, which — bound to the target itself — has already run by the time a bubbling handler sees the
 * event. Stopping propagation during the capture phase is what keeps the event from ever reaching it, so moving
 * between cells does not also spin the numbers on the way past.
 */
const claimKey = (event: KeyboardEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

/**
 * Builds the form's keydown handler. Attach it to any ancestor of the fields **in the capture phase** — it only acts
 * on events that came from inside one, so the header and the overlays controls teleport to the body keep their native
 * behaviour.
 */
export const createFieldKeydownHandler = (options: FieldKeydownOptions) => {
  const root = () => options.root?.() ?? document;

  const findFieldElement = (identifier: string) =>
    root().querySelector<HTMLElement>(`[${FIELD_IDENTIFIER_ATTRIBUTE}="${identifier}"]`);

  /** The virtualized list only mounts what is on screen, so a field scrolled to from off-screen takes a frame or two
   * to appear. */
  const waitForFieldElement = async (identifier: string) => {
    for (let attempt = 0; attempt < 5; attempt += 1) {
      await nextFrame();
      const element = findFieldElement(identifier);
      if (element) {
        return element;
      }
    }
    return null;
  };

  /** A field can be tabbed to when it is rendered — `expanded` is false for the fields of a collapsed section — and
   * when it is the kind of field that has an input at all. */
  const isTabbableField = (field: NavigableField) =>
    field.expanded !== false && !options.skipTypes.includes(field.type);

  /**
   * Moves focus into the next (or previous) field, scrolling the list when that field has not been rendered yet.
   * Fields that turn out to have no focusable input — a read-only one, say — are passed over rather than swallowing
   * the keypress.
   */
  const moveToAdjacentField = async (identifier: string, step: 1 | -1) => {
    const fields = options.fields();
    const current = fields.findIndex(field => field.identifier === identifier);
    if (current === -1) {
      return false;
    }

    for (let next = current + step; next >= 0 && next < fields.length; next += step) {
      const candidate = fields[next];
      if (!isTabbableField(candidate)) {
        continue;
      }

      let element = findFieldElement(candidate.identifier);
      if (!element) {
        options.scrollToIndex(next);
        element = await waitForFieldElement(candidate.identifier);
      }

      const focusables = element ? getFocusableElements(element) : [];
      if (focusables.length === 0) {
        continue;
      }

      // Forward lands on the field's first input, backward on its last, so a run of Shift+Tab reads as the reverse of
      // a run of Tab rather than skipping the inputs the user passed on the way in.
      const landing = step === 1 ? focusables[0] : focusables[focusables.length - 1];
      // `focus` scrolls the field into view on its own, honouring the scroll margin that keeps it clear of the header.
      landing.focus();
      placeCaret(landing, step === 1 ? 'start' : 'end');
      return true;
    }
    return false;
  };

  return async (event: KeyboardEvent) => {
    // Nothing is editable in a read-only entry, so tabbing belongs to the browser — it has to be able to reach the
    // rest of the page.
    if (options.isReadOnly()) {
      return;
    }

    const target = event.target as HTMLElement | null;
    const fieldElement = target?.closest<HTMLElement>(`[${FIELD_IDENTIFIER_ATTRIBUTE}]`);
    // Focus is in the header, or in an overlay a control has teleported to the body: leave the key alone.
    if (!target || !fieldElement) {
      return;
    }
    const identifier = fieldElement.getAttribute(FIELD_IDENTIFIER_ATTRIBUTE)!;

    if (event.key === 'Tab') {
      // An open dropdown or date panel owns Tab — that is how focus moves into it.
      if (hasOpenOverlay(target)) {
        return;
      }

      // A grid field is crossed with Tab, cell by cell; only at its first and last cell does the key come back to the
      // form.
      const fieldType = fieldElement.getAttribute(FIELD_TYPE_ATTRIBUTE);
      if (fieldType !== null && options.gridTypes.includes(fieldType)) {
        const cells = getFocusableElements(fieldElement);
        const position = cells.indexOf(target);
        const atEdge = event.shiftKey ? position <= 0 : position === cells.length - 1;
        if (!atEdge) {
          return;
        }
      }

      // Claim the key only when there is a field to move to, so Tab out of the last field still reaches the rest of
      // the page.
      const fields = options.fields();
      const current = fields.findIndex(field => field.identifier === identifier);
      const step = event.shiftKey ? -1 : 1;
      const hasCandidate = fields.some(
        (field, index) =>
          (step === 1 ? index > current : index < current) && isTabbableField(field)
      );
      if (current === -1 || !hasCandidate) {
        return;
      }

      // Only the default focus move is taken, not the event: InputNumber commits and reformats its value from its own
      // Tab handler, and cutting that off would lose what the user just typed.
      event.preventDefault();
      await moveToAdjacentField(identifier, step);
      return;
    }

    // Shift + a vertical arrow walks a table column. It is a no-op anywhere else, since only a cell has a row below it,
    // so it costs the other fields nothing.
    if (event.shiftKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
      const cellTarget = verticalCellTarget(target, event.key, fieldElement);
      if (!cellTarget) {
        return;
      }
      claimKey(event);
      cellTarget.focus();
      placeCaret(cellTarget, 'end');
      return;
    }

    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }
    if (consumesArrowKey(target, event.key)) {
      return;
    }

    const focusables = getFocusableElements(fieldElement);
    const index = nextInputIndex(focusables, target, event.key);
    // At the field's edge the arrow does nothing: leaving the field is Tab's job, and wrapping around would hide that.
    if (index === undefined) {
      return;
    }

    claimKey(event);
    focusables[index].focus();
    placeCaret(focusables[index], event.key === 'ArrowRight' ? 'start' : 'end');
  };
};
